import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Plus, Search, Landmark } from 'lucide-react';
import api from '../lib/api';
import { AssetSummaryCards } from '../components/assets/AssetSummaryCards';
import { AssetTable } from '../components/assets/AssetTable';
import { AssetFormDialog } from '../components/assets/AssetFormDialog';
import { AssetScheduleDialog } from '../components/assets/AssetScheduleDialog';
import { AssetDisposeDialog } from '../components/assets/AssetDisposeDialog';
import { CATEGORY_LABELS } from '../components/assets/assetConstants';

export default function FixedAssetsPage() {
  const [data, setData] = useState({ assets: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [editing, setEditing] = useState(null);
  const [scheduleId, setScheduleId] = useState(null);
  const [disposing, setDisposing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search.trim()) params.q = search.trim();
    if (status) params.status = status;
    api.get('/assets', { params })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load assets'))
      .finally(() => setLoading(false));
  }, [search, status]);

  useEffect(() => { load(); }, [load]);

  const remove = async (a) => {
    if (!window.confirm(`Remove "${a.name}" from the register?`)) return;
    try { await api.delete(`/assets/${a.asset_id}`); toast.success('Asset removed'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-5 max-w-6xl" data-testid="assets-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Fixed Asset Register</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Equipment, furniture and property with automatic depreciation</p>
        </div>
        <Button onClick={() => setEditing({})} className="gap-1.5" data-testid="asset-add-btn">
          <Plus className="h-4 w-4" /> Add Asset
        </Button>
      </div>

      <AssetSummaryCards summary={data.summary || {}} />

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, serial, location..."
            className="pl-9 h-9 text-sm" data-testid="asset-search" />
        </div>
        <Select value={status || '__all'} onValueChange={(v) => setStatus(v === '__all' ? '' : v)}>
          <SelectTrigger className="w-36 h-9" data-testid="asset-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="disposed">Disposed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : data.assets.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm" data-testid="assets-empty">
          <Landmark className="h-8 w-8 mx-auto mb-2 opacity-40" />
          {search || status ? 'No assets match your filters' : 'No assets yet. Click "Add Asset" to register your first one.'}
        </div>
      ) : (
        <AssetTable assets={data.assets} labels={CATEGORY_LABELS}
          onEdit={setEditing} onSchedule={(a) => setScheduleId(a.asset_id)} onDispose={setDisposing} onDelete={remove} />
      )}

      <AssetFormDialog asset={editing} onClose={() => setEditing(null)} onSaved={load} />
      <AssetScheduleDialog assetId={scheduleId} onClose={() => setScheduleId(null)} />
      <AssetDisposeDialog asset={disposing} onClose={() => setDisposing(null)} onSaved={load} />
    </div>
  );
}
